import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { createAppAsyncThunk } from "../../app/thunkWithTypes";
import { createPortfolio, getPortfoliosByUser } from "../../api/client";
import Portfolio from "../../api/models/portfolio";
import { RootState } from "../../app/store";
import { instanceToPlain, plainToInstance } from "class-transformer";
import PortfolioComposite from "../../api/models/portfolioComposite";
import { upsertComposite } from "./edit/command-bar/edit-composite-slice";
import { removeComposite, removePosition } from "./edit/command-bar/command-bar-slice";
import Position from "../../api/models/portfolioPosition";
import { upsertPosition } from "./edit/command-bar/edit-position-slice";
import Category from "../../api/models/category";
import { selectUser } from "../user/user-slice";
import User from "../../api/models/user";

interface PortfoliosState {
  portfolios: object[]
  status: 'idle' | 'pending' | 'succeeded' | 'failed'
  error: string | null
}

const initialState: PortfoliosState = {
  portfolios: [],
  status: 'idle',
  error: null
}

export const fetchPortfolios = createAppAsyncThunk('portfolios/fetchPortfolios', async (accessToken: string, { getState }) => {
  const user = plainToInstance(User, selectUser(getState()));
  if (user) {
    let portfolios = await getPortfoliosByUser(accessToken, user.id);
    if (portfolios.length === 0) {
      const baseCategory = new Category(undefined, 'CONSOLIDADO', 'Categoria base de todo portfolio.', user.id);
      const baseComposite = new PortfolioComposite(undefined, '', baseCategory, [], 1, undefined);
      const basePortfolio = new Portfolio(undefined, user.id, 'PORTFOLIO INICIAL', 'Portfolio criado automaticamente para você pelo Rebalancear.', baseComposite);
      await createPortfolio(accessToken, user.id, basePortfolio);
      portfolios = await getPortfoliosByUser(accessToken, user.id);
    }
    portfolios.forEach(p => p.recalculateValues());
    portfolios.forEach(p => p.portfolioComposite?.sort());
    return Object(instanceToPlain(portfolios));
  } else {
    console.error('An attempt to retrieve the portfolios without a user occurred.')
  }
}, {
  condition(arg, thunkApi) {
    const postsStatus = selectStatus(thunkApi.getState())
    if (postsStatus !== 'idle') {
      return false;
    }
  }
})

const portfoliosSlice = createSlice({
  name: 'portfolios',
  initialState,
  reducers: {
    recalculatePortfolio(state, action: PayloadAction<{ portfolioId: string }>) {
      const { portfolioId } = action.payload;
      const portfolios = plainToInstance(Portfolio, state.portfolios);
      portfolios.find(portfolio => portfolio.id === portfolioId)?.recalculateValues();
      portfolios.find(portfolio => portfolio.id === portfolioId)?.portfolioComposite?.sort();
      state.portfolios = Object(instanceToPlain(portfolios));
    }
  },
  extraReducers: builder => {
    builder
      .addCase(fetchPortfolios.pending, (state, action) => {
        state.status = 'pending'
      })
      .addCase(fetchPortfolios.fulfilled, (state, action) => {
        state.status = 'succeeded'
        state.portfolios.push(...action.payload)
      })
      .addCase(fetchPortfolios.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.error.message ?? 'Unknown Error'
      })
      .addCase(upsertPosition.fulfilled, (state, action) => {
        try {
          const position = plainToInstance(Position, action.payload);
          const portfolios = plainToInstance(Portfolio, state.portfolios);
          let updated = false;
          if (portfolios.map(portfolio => portfolio.updateComponent(position)).reduce((acc, cur) => acc = acc || cur, false)) {
            updated = true;
          } else if (portfolios.map(portfolio => portfolio.addComponent(position)).reduce((acc, cur) => acc = acc || cur, false)) {
            updated = true;
          }
          if (updated) {
            portfolios.forEach(portfolio => {
              portfolio.recalculateValues();
              portfolio.portfolioComposite?.sort();
            });
            state.portfolios = Object(instanceToPlain(portfolios));
          }
        } catch (e) {
          console.log(e);
        }
      })
      .addCase(upsertComposite.fulfilled, (state, action) => {
        try {
          const composite = plainToInstance(PortfolioComposite, action.payload);
          const portfolios = plainToInstance(Portfolio, state.portfolios);
          let updated = false;
          if (portfolios.map(portfolio => portfolio.updateComponent(composite)).reduce((acc, cur) => acc = acc || cur, false)) {
            updated = true;
          } else if (portfolios.map(portfolio => portfolio.addComponent(composite)).reduce((acc, cur) => acc = acc || cur, false)) {
            updated = true;
          }
          if (updated) {
            portfolios.forEach(portfolio => {
              portfolio.recalculateValues();
              portfolio.portfolioComposite?.sort();
            });
            state.portfolios = Object(instanceToPlain(portfolios));
          }
        } catch (e) {
          console.log(e);
        }
      })
      .addCase(removeComposite.fulfilled, (state, action) => {
        if (action.payload) {
          const compositeId = plainToInstance(PortfolioComposite, action.payload).id;
          const portfolios = plainToInstance(Portfolio, state.portfolios);
          if (compositeId && portfolios.map(portfolio => portfolio.removeComponent(compositeId)).reduce((acc, cur) => acc = acc || cur, false)) {
            portfolios.forEach(portfolio => {
              portfolio.recalculateValues();
              portfolio.portfolioComposite?.sort();
            });
            state.portfolios = Object(instanceToPlain(portfolios));
          }
        }
      })
      .addCase(removePosition.fulfilled, (state, action) => {
        if (action.payload) {
          const positionId = plainToInstance(Position, action.payload).id;
          const portfolios = plainToInstance(Portfolio, state.portfolios);
          if (positionId && portfolios.map(portfolio => portfolio.removeComponent(positionId)).reduce((acc, cur) => acc = acc || cur, false)) {
            portfolios.forEach(portfolio => {
              portfolio.recalculateValues();
              portfolio.portfolioComposite?.sort();
            });
            state.portfolios = Object(instanceToPlain(portfolios));
          }
        }
      })
  }
});

// Export selectors
export const selectPortfolios = createSelector([(state: RootState) => state.portfolios.portfolios], (portfolios) => {
  return plainToInstance(Portfolio, portfolios);
})
export const selectStatus = (state: RootState) => state.portfolios.status;

// Export the auto-generated action creator with the same name
export const { recalculatePortfolio } = portfoliosSlice.actions;

// Export the generated reducer function
export default portfoliosSlice.reducer;